import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, ImageOff } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useClientStore } from '@/client/stores/client';
import { cn } from '@/lib/utils';
import type { VirtualTourStatus } from '@/client/api/listing-management/virtual-tours.query';

/** Tour status → badge styling. Matches the backend's `status` enum. */
const STATUS_STYLES: Record<string, string> = {
  active: 'border-transparent bg-emerald-100 text-emerald-700',
  processing: 'border-transparent bg-blue-100 text-blue-700',
  inactive: 'border-transparent bg-gray-100 text-gray-600',
  archived: 'border-transparent bg-gray-100 text-gray-500',
  failed: 'border-transparent bg-red-100 text-red-700',
  quota_blocked: 'border-transparent bg-amber-100 text-amber-800',
  unlinked: 'border-transparent bg-purple-100 text-purple-700',
  public: 'border-transparent bg-emerald-100 text-emerald-700',
  unlisted: 'border-transparent bg-amber-100 text-amber-800',
  private: 'border-transparent bg-gray-100 text-gray-600',
  ok: 'border-transparent bg-emerald-100 text-emerald-700',
  completed: 'border-transparent bg-emerald-100 text-emerald-700',
  success: 'border-transparent bg-emerald-100 text-emerald-700',
  error: 'border-transparent bg-red-100 text-red-700',
  blocked: 'border-transparent bg-amber-100 text-amber-800',
  running: 'border-transparent bg-blue-100 text-blue-700',
  queued: 'border-transparent bg-blue-100 text-blue-700',
};

export const StatusBadge: React.FC<{ status: VirtualTourStatus | string | null | undefined }> = ({ status }) => (
  <Badge className={cn('capitalize', STATUS_STYLES[status ?? ''] ?? 'border-transparent bg-gray-100 text-gray-600')}>
    {(status ?? 'unknown').replace(/_/g, ' ')}
  </Badge>
);

export const fmtDate = (v: string | null | undefined): string => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

export const pct = (n: number | null | undefined): string =>
  n == null || Number.isNaN(n) ? '—' : `${Math.round(n * 100)}%`;

const ThumbPlaceholder: React.FC<{ alt: string }> = ({ alt }) => (
  <div
    className="flex h-12 w-16 items-center justify-center rounded border border-dashed border-gray-200 bg-gray-50 text-gray-300"
    role="img"
    aria-label={alt}
  >
    <ImageOff className="h-4 w-4" />
  </div>
);

export const Thumb: React.FC<{ src?: string | null; alt: string; onError?: () => void }> = ({ src, alt, onError }) => {
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => setFailed(false), [src]);
  if (!src || failed) return <ThumbPlaceholder alt={alt} />;
  return (
    <img
      src={src}
      alt={alt}
      className="h-12 w-16 rounded border border-gray-200 object-cover"
      onError={() => {
        setFailed(true);
        onError?.();
      }}
    />
  );
};

export const matterportShowUrl = (id: string) => `https://my.matterport.com/show/?m=${encodeURIComponent(id)}`;

/** Cover for a linked listing. ponytail: one assets call per row; drop this when the list payload includes a cover url. */
export function useListingCover(eid: number | null | undefined) {
  const request = useClientStore((s) => s.request);
  const { data } = useQuery({
    queryKey: ['listing-cover', eid],
    enabled: eid != null,
    queryFn: async () => {
      const raw = await request<any>(`/api/admin/entities/${eid}/assets`);
      const list: any[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : raw?.assets ?? raw?.data?.assets ?? [];
      const imgs = (Array.isArray(list) ? list : []).filter(
        (a) => a && !a.deleted && String(a.mime_type || '').startsWith('image/')
      );
      const pick = imgs.find((a) => a.cover || a.default) || imgs[0];
      return (pick?.public_url as string | undefined) ?? null;
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  return data ?? null;
}

export const LinkedListing: React.FC<{ eid: number; name: string | null; onOpen: () => void }> = ({ eid, name, onOpen }) => {
  const src = useListingCover(eid);
  const label = name || `#${eid}`;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      className="flex min-w-0 items-center gap-2 text-left"
    >
      <Thumb src={src} alt={label} />
      <span className="min-w-0 text-blue-600 hover:underline">{label}</span>
    </button>
  );
};

export const OpenInMatterport: React.FC<{ id: string | null | undefined; label?: string }> = ({ id, label = 'Open in Matterport' }) =>
  id ? (
    <a
      href={matterportShowUrl(id)}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-blue-600 hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      {label} <ExternalLink className="h-3 w-3" />
    </a>
  ) : null;

export const NO_PLAN_TIP =
  'This listing has no plan or promotion that includes a virtual tour. Tours are meant for listings on a plan such as Homes+.';

/** Amber "No eligible plan" (plan_eligible === false) and "Linked by bypass" (source === 'admin_bypass'). */
export const EligibilityBadges: React.FC<{ planEligible?: boolean | null; source?: string | null }> = ({ planEligible, source }) => (
  <>
    {planEligible === false && (
      <Badge title={NO_PLAN_TIP} className="border-transparent bg-amber-100 text-amber-800">
        No eligible plan
      </Badge>
    )}
    {source === 'admin_bypass' && (
      <Badge title="Linked by an admin past the plan entitlement." className="border-transparent bg-violet-100 text-violet-700">
        Linked by bypass
      </Badge>
    )}
  </>
);

export const allowanceText = (a: { limit: number; used: number; allowance_source: string }): string =>
  a.limit === -1
    ? `${a.used} virtual tour(s) used, unlimited (${a.allowance_source})`
    : `${a.used} of ${a.limit} virtual tours allowed by the listing's plan (${a.allowance_source})`;
